/* eslint-disable react-refresh/only-export-components -- TanStack routes export Route beside page components. */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ConvexProvider, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { StageMessage } from "@/components/visit/stage-message";
import { VisitorStage, type PublicWalk } from "@/components/visit/visitor-stage";
import { openConvex } from "@/lib/convex";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    /** The app's web view, when the page is open in it. */
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

/** What the page asks of the app around it: to close the walk, or to show a piece or the room's pieces. */
type ToApp = { type: "close" } | { type: "piece"; id: string } | { type: "pieces" };

function toApp(message: ToApp) {
  window.ReactNativeWebView?.postMessage(JSON.stringify(message));
}

export const Route = createFileRoute("/rooms/$roomId/walk")({
  // ?in=app: opened inside the app, which has the close button and the pieces' sheets of its own. `top`
  // and `bottom` are what the phone keeps for itself above and under the page, in px.
  validateSearch: (search): { in?: "app"; top?: number; bottom?: number } => ({
    in: search.in === "app" ? "app" : undefined,
    top: typeof search.top === "number" && Number.isFinite(search.top) ? Math.max(0, search.top) : undefined,
    bottom: typeof search.bottom === "number" && Number.isFinite(search.bottom) ? Math.max(0, search.bottom) : undefined,
  }),
  component: OpenWalkPage,
});

/** Read as nobody: the page is the same for a visitor signed in or not, and must not wait for a sign-in. */
function OpenWalkPage() {
  return (
    <ConvexProvider client={openConvex()}>
      <WalkPage />
    </ConvexProvider>
  );
}

/**
 * A room someone put on their page, to walk through: open to anyone, signed
 * in or not, and only ever what its copy shows. When the room is taken off
 * the page, or its 3D is, the walk stops then and there.
 */
function WalkPage() {
  const { t } = useTranslation("explore");
  const { t: tc } = useTranslation();
  const { t: tv } = useTranslation("visit");
  const { roomId } = Route.useParams();
  const { in: opener, top, bottom } = Route.useSearch();
  const inApp = opener === "app";
  const walk = useQuery(api.publicRooms.walk, { id: roomId });
  // Whose room it was, to say who took it off once it is gone.
  const [seen, setSeen] = useState<PublicWalk | null>(null);
  if (walk && walk.id !== seen?.id) setSeen(walk);

  return (
    <main
      className="relative h-svh w-full overflow-hidden bg-[#0e0d0c] text-white"
      // As on the owner's visit: what the device keeps under the page, the app says; a browser's safe area says for itself.
      style={
        {
          "--inset-bottom": inApp ? `${bottom ?? 0}px` : "env(safe-area-inset-bottom, 0px)",
          "--stage-head": inApp ? `${(top ?? 0) + 60}px` : "4rem",
        } as React.CSSProperties
      }
    >
      {walk === undefined ? (
        <StageMessage>{tv("stage.loadingViewer")}</StageMessage>
      ) : walk === null ? (
        <StageMessage>
          <p className="font-display text-[1.75rem] italic leading-tight text-white">
            {t("walk.gone")}
            <span className="not-italic text-accent">.</span>
          </p>
          <p className="max-w-xs text-sm text-white/65 text-balance">
            {seen ? t("walk.goneBody", { name: seen.owner }) : t("walk.goneUnknown")}
          </p>
          {inApp ? (
            <button
              type="button"
              onClick={() => toApp({ type: "close" })}
              className="pointer-events-auto mt-2 h-11 rounded-full bg-[#fcf7f0] px-6 text-[0.9375rem] font-medium text-[#1c1611]"
            >
              {tc("actions.close")}
            </button>
          ) : null}
        </StageMessage>
      ) : (
        <>
          <VisitorStage
            key={walk.id}
            walk={walk}
            onPiece={inApp ? (id) => toApp({ type: "piece", id }) : undefined}
            onPieces={inApp ? () => toApp({ type: "pieces" }) : undefined}
          />
          {/* Whose room it is: beside the app's close button (16px in, 44px of it), or at the corner of a browser's page. */}
          <div
            className={cn(
              "pointer-events-none absolute flex h-11 max-w-[calc(100%-6rem)] items-center gap-2.5 [text-shadow:0_1px_6px_rgba(0,0,0,0.35)]",
              inApp ? "start-[4.375rem]" : "start-5 top-3",
            )}
            style={inApp ? { top: (top ?? 0) + 8 } : undefined}
          >
            <span
              aria-hidden
              className="grid size-8 shrink-0 place-items-center rounded-full border border-[#fcf7f0]/20 bg-[#fcf7f0]/15 font-display text-lg leading-none"
            >
              {walk.owner.slice(0, 1)}
            </span>
            <span className="min-w-0">
              <span className="block truncate font-display text-[1.1875rem] italic leading-tight">{walk.name}</span>
              <span className="block truncate text-xs text-[#fcf7f0]/75">{walk.owner}</span>
            </span>
          </div>
        </>
      )}
    </main>
  );
}
